import React from 'react';
import { Grid } from '@mui/material';
// eslint-disable-next-line import/no-cycle
import {
	ModuleSearchField,
	SearchSelectField,
	SimpleTextField,
	useSettings,
	DateTimeField,
	DateField,
	BooleanField,
	Utils,
	useUserData
} from 'app/main/components';
import { type DetailsSectionInfoContentProps } from 'app/main/components/page-layouts';
import type { Moment } from 'moment';
import { FormattedMessage } from 'react-intl';
import { parseDate, today } from 'app/main/components/Utils';

export default function LeaveDetailsSectionInfo(props: DetailsSectionInfoContentProps) {
	const { sysSettings, t } = useSettings('leave');
	const userData = useUserData();

	const [hasRecovery, setHasRecovery] = React.useState(!!props.model.lvty_recovery_hours);

	const afterChange = changeModel => {
		setHasRecovery(changeModel?.lvty_recovery_hours);
	};

	const minTimeDate = Utils.parseDate(userData.user_start_time);
	const minTimeHour = minTimeDate?.get('hour') || 0;
	const minTimeMinute = minTimeDate?.get('minute') || 0;

	const maxTimeDate = Utils.parseDate(userData.user_end_time);
	const maxTimeHour = maxTimeDate?.get('hour') || 0;
	const maxTimeMinute = maxTimeDate?.get('minute') || 0;

	const dateChange = (name: string, value: string | Moment | null) => {
		const time = Utils.parseDate(props.model[name]);
		const parsedValue = Utils.parseDate(value);

		if (parsedValue !== undefined) {
			parsedValue.set('hour', time?.hour() || minTimeHour);
			parsedValue.set('minute', time?.minute() || 0);
			props.sendChange(name, parsedValue.utc());
		} else {
			props.sendChange(name, null);
		}
	};

	const afterUseWholeDayChange = () => {
		const newDateFrom = Utils.parseDate(props.model.leav_d_from)
			?.set('hour', minTimeHour)
			.set('minute', minTimeMinute);
		const newDateTo = Utils.parseDate(props.model.leav_d_to || props.model.leav_d_from)
			?.set('hour', maxTimeHour)
			.set('minute', maxTimeMinute);

		props.sendChange('leav_d_from', newDateFrom || null);
		props.sendChange('leav_d_to', newDateTo || null);
	};

	const dateValidationErrors = {
		isDefaultRequiredValue: t('ERROR_TEXT_REQUIRED'),
		minDate: (
			<FormattedMessage
				id="minDateError"
				defaultMessage={t('ERROR_MIN_DATE')}
				values={{ value: parseDate(props.model.leav_d_from)?.format('DD/MM/yyyy') }}
			/>
		)
	};

	const dateTimeValidationsTo = {
		minDateTime: parseDate(props.model.leav_d_from),
		minDate: parseDate(props.model.leav_d_from)
	};

	const dateTimeValidationErrorsTo = {
		isDefaultRequiredValue: t('ERROR_TEXT_REQUIRED'),
		minDateTime: (
			<FormattedMessage
				id="minDateTimeError"
				defaultMessage={t('ERROR_MIN_DATE')}
				values={{
					value: parseDate(props.model.leav_d_from)?.format('DD/MM/yyyy HH:mm')
				}}
			/>
		)
	};

	return (
		<>
			<Grid size={12}>
				{/* TITLE */}
				<SimpleTextField
					name="leav_title"
					label={t('DETAILS_LEAVE_FIELD_TITLE')}
					value={props.model.leav_title}
					isEdit={props.isEdit}
					sendChange={props.sendChange}
					logProps={props.logProps}
					required
					validationError={t('ERROR_TEXT_REQUIRED')}
				/>
			</Grid>
			<Grid size={{ sm: 6, xs: 12 }}>
				{/* STATUS */}
				<SearchSelectField
					name="leav_lvst_id"
					label={t('DETAILS_LEAVE_FIELD_STATUS')}
					value={props.model.leav_lvst_id}
					systable="leave_statuses"
					isEdit={props.isEdit}
					sendChange={props.sendChange}
					logProps={props.logProps}
				/>
			</Grid>
			<Grid size={{ sm: 6, xs: 12 }}>
				{/* TYPE */}
				<SearchSelectField
					name="leav_lvty_id"
					label={t('DETAILS_LEAVE_FIELD_TYPE')}
					value={props.model.leav_lvty_id}
					systable="leave_types"
					isEdit={props.isEdit}
					afterChange={afterChange}
					sendChange={props.sendChange}
					logProps={props.logProps}
				/>
			</Grid>
			<Grid size={{ sm: 6, xs: 12 }}>
				{/* USER */}
				<ModuleSearchField
					name="leav_user_uid"
					label={t('DETAILS_LEAVE_FIELD_USER')}
					module="users"
					uid={props.model.leav_user_uid}
					isEdit={props.isEdit}
					sendChange={props.sendChange}
					logProps={props.logProps}
				/>
			</Grid>
			<Grid size={{ md: 6, xs: 12 }}>
				{/* ALL DAY */}
				<BooleanField
					name="leav_use_whole_day"
					label={t('DETAILS_LEAVE_FIELD_WHOLE_DAY')}
					value={props.model.leav_use_whole_day}
					isEdit={props.isEdit}
					sendChange={props.sendChange}
					afterChange={afterUseWholeDayChange}
				/>
			</Grid>
			{/* FROM DATE */}
			<Grid size={{ md: 6, xs: 12 }}>
				{!props.model.leav_use_whole_day ? (
					<DateTimeField
						name="leav_d_from"
						label={t('DETAILS_LEAVE_FIELD_DATE_FROM')}
						value={props.model.leav_d_from}
						isEdit={props.isEdit}
						sendChange={props.sendChange}
						afterChange={newDate => props.sendChange('leav_d_to', newDate || null)}
						required
						validationError={t('ERROR_TEXT_REQUIRED')}
					/>
				) : (
					<DateField
						name="leav_d_from"
						label={t('DETAILS_LEAVE_FIELD_DATE_FROM')}
						value={props.model.leav_d_from}
						isEdit={props.isEdit}
						sendChange={dateChange}
						afterChange={newDate => {
							const newDateTo = newDate?.clone().local();
							newDateTo?.set('hour', maxTimeHour);
							newDateTo?.set('minute', maxTimeMinute);
							props.sendChange('leav_d_to', newDateTo?.utc() || null);
						}}
						required
						validationError={t('ERROR_TEXT_REQUIRED')}
					/>
				)}
			</Grid>
			{/* TO DATE */}
			<Grid size={{ md: 6, xs: 12 }}>
				{!props.model.leav_use_whole_day ? (
					<DateTimeField
						name="leav_d_to"
						label={t('DETAILS_LEAVE_FIELD_DATE_TO')}
						value={props.model.leav_d_to}
						isEdit={props.isEdit}
						sendChange={props.sendChange}
						required
						validations={dateTimeValidationsTo}
						validationErrors={dateTimeValidationErrorsTo}
					/>
				) : (
					<DateField
						name="leav_d_to"
						label={t('DETAILS_LEAVE_FIELD_DATE_TO')}
						value={props.model.leav_d_to}
						isEdit={props.isEdit}
						sendChange={dateChange}
						required
						validations={{ minDate: props.model.leav_d_from }}
						validationErrors={dateValidationErrors}
					/>
				)}
			</Grid>
			{hasRecovery && (
				<>
					<Grid size={{ sm: 6, xs: 12 }}>
						{/* RECOVERY DATE FROM */}
						<DateTimeField
							name="leav_recovery_d_from"
							label={t('DETAILS_LEAVE_FIELD_RECOVERY_DATE_FROM')}
							value={props.model.leav_recovery_d_from}
							isEdit={props.isEdit && props.model.leav_recovery_hours}
							sendChange={props.sendChange}
							logProps={props.logProps}
						/>
					</Grid>
					<Grid size={{ sm: 6, xs: 12 }}>
						{/* RECOVERY DATE TO */}
						<DateTimeField
							name="leav_recovery_d_to"
							label={t('DETAILS_LEAVE_FIELD_RECOVERY_DATE_TO')}
							value={props.model.leav_recovery_d_to}
							isEdit={props.isEdit && props.model.leav_recovery_hours}
							sendChange={props.sendChange}
							logProps={props.logProps}
						/>
					</Grid>
				</>
			)}
			<Grid size={{ sm: 6, xs: 12 }}>
				{/* ACCEPTED BY USER */}
				<ModuleSearchField
					name="leav_user_assigned_uid"
					label={t('DETAILS_LEAVE_FIELD_ASSIGNED_USER')}
					module="users"
					uid={props.model.leav_user_assigned_uid}
					isEdit={props.isEdit}
					sendChange={props.sendChange}
					logProps={props.logProps}
				/>
			</Grid>
			<Grid size={{ sm: 6, xs: 12 }}>
				{/* REJECTED REASON */}
				<SimpleTextField
					name="leav_rejected_reason"
					label={t('DETAILS_LEAVE_FIELD_REJECTED_REASON')}
					value={props.model.leav_rejected_reason}
					isEdit={props.isEdit}
					sendChange={props.sendChange}
					logProps={props.logProps}
				/>
			</Grid>
		</>
	);
}
